"use client";

import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
} from "react";
import { useTheme, useMediaQuery } from "@mui/material";

const TTSContext = createContext(null);

export const TTSProvider = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [currentTextId, setCurrentTextId] = useState(null);
  const [speechSupported, setSpeechSupported] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (typeof window !== "undefined" && "Audio" in window && !isMobile) {
      setSpeechSupported(true);
    } else {
      setSpeechSupported(false);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsSpeaking(false);
      setCurrentTextId(null);
      setIsGeneratingAudio(false);
    }
  }, [isMobile]);

  const stopCurrent = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsSpeaking(false);
      setCurrentTextId(null);
    }
  };

  const speak = async (text, textId, autoPlay = false) => {
    if (!speechSupported || !text) return;

    if (isSpeaking && currentTextId === textId) {
      stopCurrent();
      return;
    }

    stopCurrent();

    try {
      setIsGeneratingAudio(true);
      setCurrentTextId(textId);

      const cleanText = text
        .replace(/\*\*/g, "")
        .replace(/\*/g, "")
        .replace(/#{1,6}\s/g, "")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/\n\n/g, ". ")
        .replace(/\n/g, " ")
        .trim();

      const response = await fetch("/api/support/text-to-speech", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text: cleanText }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate speech");
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

      if (!audioRef.current) {
        audioRef.current = new Audio();
      }

      audioRef.current.src = audioUrl;
      audioRef.current.volume = 1.0;

      audioRef.current.onended = () => {
        setIsSpeaking(false);
        setCurrentTextId(null);
        URL.revokeObjectURL(audioUrl);
      };

      audioRef.current.onerror = () => {
        setIsSpeaking(false);
        setCurrentTextId(null);
        setIsGeneratingAudio(false);
        URL.revokeObjectURL(audioUrl);
      };

      const playPromise = audioRef.current.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsSpeaking(true);
            setIsGeneratingAudio(false);
          })
          .catch((error) => {
            console.error("Audio playback failed:", error);
            setIsSpeaking(false);
            setCurrentTextId(null);
            setIsGeneratingAudio(false);
          });
      }
    } catch (error) {
      console.error("Text-to-speech error:", error);
      setIsSpeaking(false);
      setCurrentTextId(null);
      setIsGeneratingAudio(false);
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <TTSContext.Provider
      value={{
        speak,
        stopSpeaking: stopCurrent,
        isSpeaking,
        isGeneratingAudio,
        speechSupported,
        currentTextId,
      }}
    >
      {children}
    </TTSContext.Provider>
  );
};

export const useTTSContext = () => {
  const context = useContext(TTSContext);
  if (!context) {
    throw new Error("useTTSContext must be used within TTSProvider");
  }
  return context;
};
